import React from 'react';
import styled from 'styled-components';

import { SortingProps } from '@block-explorer/ui';

import {
  FlexColumn,
  FlexRow,
  formatNumber,
  SvgIcon,
  useMatchMedia,
} from '@make-software/cspr-ui';
import { BodyText } from '@block-explorer/ui';

import { ApiPaginatedGetResult } from '../../hooks/event-store-hooks';
import { usePaginationState } from '../../router/navigation';
import { sortNumericFields } from '../../utils/sorting';
import { Trans, useTranslation } from 'react-i18next';
import { RowsPerPage } from './rows-per-page';
import { PaginationArrowButton, PaginationButton } from './pagination-button';
import { PaginationInput } from './pagination-input';
import { PaginationParams } from '../../services';

const Container = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    height: [80, 48],
    flexDirection: ['column', 'row', 'row'],
    justifyContent: 'space-between',
    padding: ['12px 10px', '12px 20px'],
    margin: ['0 0 10px 0', '0'],
  })
);

const MirroredSvgIcon = styled(SvgIcon)(({ theme }) => ({
  transform: 'rotate(180deg)',
}));

export const useClientPagination = <Entity extends any>({
  paginationStateKey,
  arr,
  requestParams,
}: {
  paginationStateKey: string;
  arr: Entity[] | null;
  requestParams?: Partial<PaginationParams>;
}): Pick<
  ApiPaginatedGetResult<Entity>,
  'data' | 'paginationProps' | 'sortingProps'
> => {
  const {
    currentPage: currentPageParam,
    setCurrentPage: setPaginationPage,
    perPage: perPageParam,
    setPerPage: setPaginationPerPage,
    orderBy,
    orderDirection,
    setOrder,
  } = usePaginationState(paginationStateKey);

  const { perPage, currentPage } = {
    currentPage: currentPageParam,
    perPage: perPageParam,
    ...requestParams,
  };

  const total = arr?.length || 0;

  const paginationProps: PaginationProps = {
    itemCount: total,
    pageCount: Math.ceil(total / perPage) || 0,
    currentPage: currentPage,
    setCurrentPage: (page: number) => {
      if (page > 0 && page <= total) {
        setPaginationPage(page);
      }
    },
    perPage: perPage,
    setPerPage: setPaginationPerPage,
  };

  const sortingProps: SortingProps = {
    orderBy: orderBy,
    orderDirection: orderDirection,
    setOrder,
  };

  const data =
    arr &&
    arr
      .sort((fieldA, fieldB) =>
        orderBy === null || orderBy === undefined
          ? 0
          : sortNumericFields(fieldA[orderBy], fieldB[orderBy], orderDirection)
      )
      .filter((_, index) => {
        const minIndex = (currentPage - 1) * perPage;
        const maxIndex = currentPage * perPage;
        return index >= minIndex && index < maxIndex;
      });

  return {
    data,
    paginationProps: { ...paginationProps, perPage: paginationProps.perPage },
    sortingProps,
  };
};

export interface PaginationProps {
  perPage: number;
  itemCount?: number;
  pageCount?: number;
  currentPage?: number;
  setPerPage?: (limit: number) => void;
  setCurrentPage?: (page: number) => void;
  hideRowsPerPage?: boolean;
  totalRowsLabel?: string;
}

const ROWS_PER_PAGE = [10, 25, 50, 100];

export function Pagination({
  perPage = 10,
  itemCount = 0,
  pageCount = 1,
  currentPage = 1,
  setCurrentPage = () => {},
  setPerPage = () => {},
  hideRowsPerPage = false,
  totalRowsLabel = 'total rows',
}: PaginationProps) {
  const { t } = useTranslation();
  const prevPageHandler = () => {
    setCurrentPage(currentPage - 1);
  };

  const nextPageHandler = () => {
    setCurrentPage(currentPage + 1);
  };

  const firstPageHandler = () => {
    setCurrentPage(1);
  };

  const lastPageHandler = () => {
    setCurrentPage(pageCount);
  };

  const handlePaginationChange = (page) => {
    setCurrentPage(page);
  };

  const isFirstEnabled = currentPage > 1;
  const isPrevEnabled = currentPage > 1;
  const isNextEnabled = currentPage < pageCount;
  const isLastEnabled = currentPage < pageCount;

  const onMobile = (
    <FlexColumn itemsSpacing={4}>
      <FlexRow itemsSpacing={4}>
        <PaginationButton
          color={'secondaryRed'}
          disabled={!isFirstEnabled}
          onClick={firstPageHandler}
        >
          {t('First', 'First')}
        </PaginationButton>
        <PaginationArrowButton
          color={'secondaryRed'}
          disabled={!isPrevEnabled}
          onClick={prevPageHandler}
        >
          <MirroredSvgIcon src="/assets/icons/ic-arrow-right.svg" />
        </PaginationArrowButton>
        <PaginationInput
          pageCount={pageCount}
          currentPage={currentPage}
          onChange={handlePaginationChange}
        />
        <PaginationArrowButton
          color={'secondaryRed'}
          disabled={!isNextEnabled}
          onClick={nextPageHandler}
        >
          <SvgIcon
            src="/assets/icons/ic-arrow-right.svg"
            role={'img'}
            alt={'Arrow icon right'}
          />
        </PaginationArrowButton>
        <PaginationButton
          color={'secondaryRed'}
          disabled={!isLastEnabled}
          onClick={lastPageHandler}
        >
          {t('Last', 'Last')}
        </PaginationButton>
      </FlexRow>
      {!hideRowsPerPage && (
        <RowsPerPage
          value={perPage}
          items={ROWS_PER_PAGE}
          onChange={setPerPage}
        />
      )}
    </FlexColumn>
  );

  const onAbove = (
    <FlexRow itemsSpacing={40}>
      {!hideRowsPerPage && (
        <RowsPerPage
          value={perPage}
          items={ROWS_PER_PAGE}
          onChange={setPerPage}
        />
      )}
      <FlexRow itemsSpacing={4}>
        <PaginationButton
          disabled={!isFirstEnabled}
          onClick={firstPageHandler}
          color={'secondaryRed'}
        >
          {t('First', 'First')}
        </PaginationButton>
        <PaginationArrowButton
          color={'secondaryRed'}
          disabled={!isPrevEnabled}
          onClick={prevPageHandler}
        >
          <MirroredSvgIcon
            src="/assets/icons/ic-arrow-right.svg"
            alt={'Arrow left icon'}
          />
        </PaginationArrowButton>
        <PaginationInput
          pageCount={pageCount}
          currentPage={currentPage}
          onChange={handlePaginationChange}
        />
        <PaginationArrowButton
          color={'secondaryRed'}
          disabled={!isNextEnabled}
          onClick={nextPageHandler}
        >
          <SvgIcon
            src="/assets/icons/ic-arrow-right.svg"
            alt={'Arrow right icon'}
            role={'img'}
          />
        </PaginationArrowButton>
        <PaginationButton
          disabled={!isLastEnabled}
          onClick={lastPageHandler}
          color={'secondaryRed'}
        >
          {t('Last', 'Last')}
        </PaginationButton>
      </FlexRow>
    </FlexRow>
  );

  const responsivePagination = useMatchMedia(
    [onMobile, onAbove],
    [perPage, currentPage, pageCount, setCurrentPage]
  );

  const noData = pageCount < 1;
  if (noData) {
    return (
      <Container>
        <span>&nbsp;</span>
      </Container>
    );
  }

  return (
    <Container>
      <BodyText size={3} variation="darkGray">
        <Trans t={t}>
          {{ rows: formatNumber(itemCount) }} {totalRowsLabel}
        </Trans>
      </BodyText>
      {responsivePagination}
    </Container>
  );
}

export default Pagination;
